// Partner voucher landing page (#2357): https://app.hellohousing.de/redeem?code=HHP-7KQ4-M9XT-2RWD
//
// A partner's shop (e.g. Haus & Grund) sells a hellohousing plan and sends the customer this link.
// The page checks the code, shows the plan and its term, and redeems it after the customer has
// created an account or signed in. Redeeming starts the plan today, or extends the plan the
// account already has by the code's term (decided by the backend).

// Base libs
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

// Mantine UI libs
import { useForm } from '@mantine/form';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  Loader,
  LoadingOverlay,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import Autoload from '../neoverv/Autoload';
import { resetData, setProfileData } from '../redux/action/General/generalActions';
import { VOUCHER_STATE, findRedeemedAssignment, normalizeVoucherCode, redeemVoucher, validateVoucher } from '../helpers/voucher';
import { AUTH_ERROR, createAccount, getRecaptchaToken, renderRecaptchaChallenge, signIn } from '../helpers/accountAccess';
import { getPartner, getSubscriptionDisplayName, getSubscriptionDisplayTier, getSubscriptionFullName } from '../helpers/partners';
import { getPrepaidTermEnd, getTermLabel, isAnnualSubscription } from '../helpers/subscriptionBilling';
import { isPasswordValid } from '../helpers/passwordValidation';
import PasswordGuidanceInput from '../components/PasswordGuidanceInput';
import { LEGAL_DOCUMENT_LINKS } from '../helpers/legalConsent';
import PartnerBranding from '../components/PartnerBranding';
import SubscriptionBadgeIcon from '../neoverv/components/SVGs/SubscriptionBadgeIcon';
import classes from './Redeem.module.css';

// hellohousing's accent colour, for every button and link on this page.
const ACCENT = '#5555FF';

const STEP = {
  CHOOSE: 'CHOOSE',
  REGISTER: 'REGISTER',
  LOGIN: 'LOGIN',
  REDEEM_FAILED: 'REDEEM_FAILED',
  SUCCESS: 'SUCCESS',
};

const TEXT = {
  generalError: 'Es ist ein Fehler aufgetreten. Bitte probieren Sie es erneut oder wenden Sie sich an den hellohousing Service.',
  securityCheckFailed: 'Sicherheitsüberprüfung fehlgeschlagen. Bitte laden Sie die Seite neu.',
  challengeError: 'Es gab ein Problem mit dem Captcha.',
  invalidLogin: 'Nutzername oder Passwort sind nicht korrekt. Bitte wiederholen Sie Ihre Eingabe oder wenden Sie sich an den hellohousing Service.',
  noAccess: 'Sie haben keinen Zugriff auf diesen Bereich. Bei Fragen wenden Sie sich bitte an den hellohousing Service.',
  emailExists: 'Für diese E-Mail-Adresse gibt es bereits ein hellohousing Konto.',
  signInAfterSignUp: 'Ihr Konto wurde erstellt. Bitte melden Sie sich an, um Ihren Code einzulösen.',
  signedOut: 'Ihre Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an, um Ihren Code einzulösen.',
};

// SignUp.jsx keeps dev and local sign-ups behind this password.
const DEV_SIGNUP_PASSWORD = 'Testing9!';
const isDevHost = () => window.location.href.includes('dev') || window.location.href.includes('localhost');

const formatDate = (date) => (date ? moment(date).format('DD.MM.YYYY') : null);

// Intercom is booted on this page, but blocked scripts leave it undefined.
const intercom = (...args) => window.Intercom?.(...args);
const bootIntercom = () =>
  intercom('boot', {
    app_id: 'vvrs5bx3',
    Environment: window.location.hostname.includes('app') ? 'Production' : 'Staging',
  });

const supportLink = <Anchor c={ACCENT} href="mailto:service@hellohousing.de">hellohousing Service</Anchor>;

// What the page says about a code that can't be redeemed (`failedOnRedeem`: the check passed,
// but redeeming it then failed, e.g. because it was redeemed in another tab meanwhile).
const getProblem = (state, { voucher, partner, failedOnRedeem }) => {
  const seller = partner ? partner.name : 'den Anbieter, bei dem Sie ihn erworben haben';
  switch (state) {
    // Also where the app's "Klicken Sie hier, um einen Code einzulösen" leads, so it reads as a start, not an error.
    case VOUCHER_STATE.MISSING:
      return { title: 'Code einlösen', text: 'Keinen Code zur Einlösung erkannt. Bitte geben Sie Ihren Code ein. Sie finden ihn in Ihrer Bestellbestätigung.', canEnterCode: true };
    case VOUCHER_STATE.INVALID:
      // Refused on redeem (the backend's NOT_FOUND), there's no code to enter again.
      return failedOnRedeem
        ? { title: 'Code konnte nicht eingelöst werden', text: <>Dieser Code kann nicht eingelöst werden. Bitte wenden Sie sich an {seller} oder an den {supportLink}.</> }
        : { title: 'Code ungültig', text: 'Diesen Code konnten wir nicht finden. Bitte prüfen Sie den Code aus Ihrer Bestellbestätigung und geben Sie ihn erneut ein.', canEnterCode: true };
    case VOUCHER_STATE.REDEEMED:
      return failedOnRedeem
        ? { title: 'Code bereits eingelöst', text: 'Dieser Code wurde inzwischen bereits eingelöst.' }
        : { title: 'Code bereits eingelöst', text: 'Dieser Code wurde bereits eingelöst. Wenn Sie ihn selbst eingelöst haben, melden Sie sich einfach an.', canSignIn: true };
    case VOUCHER_STATE.EXPIRED: {
      const until = formatDate(voucher?.expiresAt);
      return { title: 'Code abgelaufen', text: <>{until ? `Dieser Code konnte bis zum ${until} eingelöst werden.` : 'Dieser Code ist abgelaufen.'} Bitte wenden Sie sich an {seller} oder an den {supportLink}.</> };
    }
    case VOUCHER_STATE.OTHER_PARTNER:
      return { title: 'Code passt nicht zu diesem Konto', text: <>Diesen Code können Sie nur mit einem Konto einlösen, das Sie über {partner ? partner.name : 'diesen Anbieter'} bei hellohousing erstellt haben. Bitte melden Sie sich mit diesem Konto an oder erstellen Sie ein neues.</>, canUseOtherAccount: true };
    case VOUCHER_STATE.CANCELLED:
      return { title: 'Code storniert', text: <>Dieser Code wurde storniert und kann nicht mehr eingelöst werden. Bitte wenden Sie sich an {seller} oder an den {supportLink}.</> };
    case VOUCHER_STATE.RATE_LIMITED:
      return { title: 'Zu viele Versuche', text: 'Bitte warten Sie einen Moment und versuchen Sie es dann erneut.', canRetry: true };
    default:
      return {
        title: failedOnRedeem ? 'Code konnte nicht eingelöst werden' : 'Code konnte nicht geprüft werden',
        text: 'Es ist ein technischer Fehler aufgetreten. Bitte versuchen Sie es in einigen Minuten erneut.',
        canRetry: true,
      };
  }
};

// The code's plan; the voucher carries the partner, its plan doesn't.
const getVoucherPlan = (voucher) => voucher?.subscriptionType && { ...voucher.subscriptionType, partnerId: voucher.partnerId };

// A plan as the app shows it after login: its tier badge, "Premium" rather than "Haus & Grund
// Premium" (the partner is shown by its branding above). `inline` sets it into a line of text.
const PlanBadge = ({ plan, inline = false }) => (
  <span className={inline ? classes.inlinePlanBadge : classes.planBadge}>
    <SubscriptionBadgeIcon name={getSubscriptionDisplayName(plan)} category={getSubscriptionDisplayTier(plan)} />
  </span>
);

// The code's plan, its term and the code itself (design draft of 03.11.2026).
const VoucherProduct = ({ voucher, code }) => {
  const plan = getVoucherPlan(voucher) || {};
  const redeemableUntil = formatDate(voucher?.expiresAt);
  return (
    <Box className={classes.product}>
      <Text c="#13135C" fw={700} className={classes.productName}>{getSubscriptionFullName(plan)}</Text>
      <Text size="md" c="#13135C" mt={12}>{isAnnualSubscription(plan) ? '12 Monate' : '1 Monat'} (vorausbezahlt)</Text>
      {/* Expiry only when the API sends it (not yet as of 2026-10-30, see helpers/voucher.js). */}
      {redeemableUntil && <Text size="md" c="#13135C" mt={4}>Einlösbar bis {redeemableUntil}</Text>}
      <Text size="sm" c="#727293" mt={8}>Code: {code}</Text>
    </Box>
  );
};

const Redeem = ({ history, location }) => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.generalReducer.profile);
  const signedInEmail = profile?.auth?.token ? profile.email : null;

  const code = normalizeVoucherCode(new URLSearchParams(location.search).get('code'));
  const [enteredCode, setEnteredCode] = useState('');
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [check, setCheck] = useState({ loading: true, state: null, voucher: null });

  const [step, setStep] = useState(STEP.CHOOSE);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null); // { text, offerSignIn? }
  const [formInfo, setFormInfo] = useState(null);
  const [redeemFailure, setRedeemFailure] = useState(null); // VOUCHER_STATE
  const [assignment, setAssignment] = useState(null);
  // The account was just created through this page, and its activation redeemed the code.
  const [redeemedAtSignup, setRedeemedAtSignup] = useState(false);

  // reCAPTCHA checkbox challenge, shown when the backend refused the invisible check's token.
  // `onSolvedRef` always holds the latest retry, so a reset widget never resends stale values.
  const challengeContainerRef = useRef(null);
  const challengeWidgetIdRef = useRef(null);
  const onSolvedRef = useRef(null);
  const [challenge, setChallenge] = useState(0); // 0: hidden, otherwise a counter to (re)render it

  const registerForm = useForm({
    initialValues: { email: '', password: '', devPassword: '', terms: false },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'),
      password: (value) => (isPasswordValid(value) ? null : 'Bitte wählen Sie ein sicheres Passwort.'),
      devPassword: (value) => (!isDevHost() || value === DEV_SIGNUP_PASSWORD ? null : 'Bitte geben Sie das Dev Passwort ein.'),
      terms: (value) => (value ? null : 'Bitte bestätigen Sie die AGB, den AVV und die Datenschutzerklärung.'),
    },
  });
  const loginForm = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Bitte geben Sie eine gültige E-Mail-Adresse ein.'),
      password: (value) => (value ? null : 'Bitte geben Sie Ihr Passwort ein.'),
    },
  });

  useEffect(() => {
    bootIntercom();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCheck({ loading: true, state: null, voucher: null });
    validateVoucher(code).then((result) => {
      if (!cancelled) {
        setCheck({ loading: false, ...result });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code, checkAttempt]);

  useEffect(() => {
    if (!challenge) {
      challengeWidgetIdRef.current = null;
      return;
    }
    if (window.grecaptcha && challengeContainerRef.current) {
      challengeWidgetIdRef.current = renderRecaptchaChallenge(challengeContainerRef.current, challengeWidgetIdRef.current, {
        onSolved: (token) => onSolvedRef.current?.(token),
        onError: () => setFormError({ text: TEXT.challengeError }),
      });
    }
  }, [challenge]);

  const voucher = check.voucher;
  const voucherPlan = getVoucherPlan(voucher);
  const partner = getPartner({ partnerId: voucher?.partnerId, subscriptionKey: voucher?.subscriptionType?.subscriptionKey });

  const goToStep = (nextStep) => {
    setFormError(null);
    setFormInfo(null);
    setChallenge(0);
    setStep(nextStep);
  };

  const showChallenge = (retry) => {
    onSolvedRef.current = retry;
    setChallenge((count) => count + 1);
  };

  // Same as the sidebar's "Abmelden", but staying on this page: Intercom starts again without the
  // previous user, for the events of the account the code is redeemed with.
  const signOut = () => {
    Autoload.Utils.clear_session_data();
    intercom('shutdown');
    dispatch(resetData());
    bootIntercom();
  };

  // `account`: the signed-in profile, { accountId, auth: { token } }.
  const redeem = async (account) => {
    setBusy(true);
    if (redeemedAtSignup) {
      const redeemedAssignment = await findRedeemedAssignment({ voucher, accountId: account?.accountId, authToken: account?.auth?.token });
      // No active plan yet (the backend may still be setting it up), but the code is redeemed:
      // confirm it without dates rather than redeeming it a second time.
      const redeemedCode = redeemedAssignment ? null : await validateVoucher(code);
      if (redeemedAssignment || redeemedCode?.state === VOUCHER_STATE.REDEEMED) {
        setBusy(false);
        setAssignment(redeemedAssignment || {});
        intercom('trackEvent', 'voucher-redeemed', { partner: partner?.name || voucher?.partnerId || null, subscriptionName: voucher?.subscriptionType?.name || null });
        goToStep(STEP.SUCCESS);
        return;
      }
      // The activation didn't assign the plan: redeem it now, as for an existing account.
    }
    const result = await redeemVoucher({ voucher, accountId: account?.accountId, authToken: account?.auth?.token });
    setBusy(false);
    if (result.state === VOUCHER_STATE.SIGNED_OUT) {
      signOut();
      goToStep(STEP.LOGIN);
      setFormInfo(TEXT.signedOut);
      return;
    }
    if (result.state) {
      setRedeemFailure(result.state);
      goToStep(STEP.REDEEM_FAILED);
      return;
    }
    setAssignment(result.assignment || {});
    intercom('trackEvent', 'voucher-redeemed', {
      partner: partner?.name || voucher?.partnerId || null,
      subscriptionName: voucher?.subscriptionType?.name || null,
    });
    goToStep(STEP.SUCCESS);
  };

  const signInAndRedeem = async ({ email, password }, challengeToken = null) => {
    setBusy(true);
    setFormError(null);
    let recaptchaToken = challengeToken;
    if (!recaptchaToken) {
      try {
        recaptchaToken = await getRecaptchaToken('login');
      } catch (error) {
        setBusy(false);
        setFormError({ text: TEXT.securityCheckFailed });
        return;
      }
    }

    let profileData;
    try {
      profileData = await signIn({ email: email.trim(), password, recaptchaToken, isRecaptchaV2: Boolean(challengeToken) });
    } catch (error) {
      setBusy(false);
      if (error.code === AUTH_ERROR.CHALLENGE_REQUIRED) {
        showChallenge((token) => signInAndRedeem({ email, password }, token));
        return;
      }
      // A ticked challenge can't be used again: hide it, so the button to send again is back.
      setChallenge(0);
      setFormError({ text: error.code === AUTH_ERROR.INVALID_LOGIN ? TEXT.invalidLogin : error.code === AUTH_ERROR.NO_ACCESS ? TEXT.noAccess : TEXT.generalError });
      return;
    }

    setChallenge(0);
    intercom('trackEvent', 'log-in');
    dispatch(setProfileData(profileData));
    await redeem(profileData);
  };

  // Creates the account linked to the voucher's partner. The customer then signs in to redeem:
  // the code is redeemed only with the credentials of the account it goes to.
  const register = async (values, challengeToken = null) => {
    setBusy(true);
    setFormError(null);
    let recaptchaToken = challengeToken;
    if (!recaptchaToken) {
      try {
        recaptchaToken = await getRecaptchaToken('signup');
      } catch (error) {
        setBusy(false);
        setFormError({ text: TEXT.securityCheckFailed });
        return;
      }
    }

    const email = values.email.trim();
    let account;
    try {
      account = await createAccount({ email, password: values.password, recaptchaToken, isRecaptchaV2: Boolean(challengeToken), partnerId: voucher?.partnerId || null, voucherId: voucher?.id || null });
    } catch (error) {
      setBusy(false);
      if (error.code === AUTH_ERROR.CHALLENGE_REQUIRED) {
        showChallenge((token) => register(values, token));
        return;
      }
      setChallenge(0);
      setFormError(error.code === AUTH_ERROR.EMAIL_EXISTS ? { text: TEXT.emailExists, offerSignIn: true } : { text: TEXT.generalError });
      return;
    }

    setChallenge(0);
    // Same attributes SignUp.jsx gives a new account.
    intercom('update', {
      email,
      Environment: window.location.hostname.includes('app') ? 'Production' : 'Staging',
      'Number of properties': '0',
      'Number of condominiums': '0',
      'Rental Unit Created': false,
      'Tenant Created': false,
      'Receipt Created': false,
      'Receipt Created Automated': false,
      'Meter Reading Conducted': false,
      'Company': '',
      'Meter Created': false,
      'Utility Billing Created': false,
      'Account Deleted': false,
      'Account Id': account.accountId,
      user_id: account.userId,
    });
    if (account.activated) {
      intercom('trackEvent', 'signed-up');
    }
    setBusy(false);
    setRedeemedAtSignup(true);
    loginForm.setValues({ email, password: '' });
    goToStep(STEP.LOGIN);
    setFormInfo(TEXT.signInAfterSignUp);
  };

  const checkEnteredCode = (event) => {
    event.preventDefault();
    const nextCode = normalizeVoucherCode(enteredCode);
    if (!nextCode) {
      return;
    }
    // The code is read from the link, so this checks the new one (and the same one again).
    history.replace(`/redeem?code=${encodeURIComponent(nextCode)}`);
    goToStep(STEP.CHOOSE);
    setCheckAttempt((count) => count + 1);
  };

  const renderChallenge = () =>
    challenge > 0 && (
      <Alert color="yellow" title="Sicherheitsüberprüfung">
        <Text size="md" mb={10}>Bitte bestätigen Sie, dass Sie ein Mensch sind, um fortzufahren.</Text>
        <Box ref={challengeContainerRef} style={{ display: 'flex', justifyContent: 'center' }} />
      </Alert>
    );

  const renderFormMessages = () => (
    <>
      {formInfo && <Alert color="blue"><Text size="md">{formInfo}</Text></Alert>}
      {formError && (
        <Alert color="red">
          <Text size="md">{formError.text}</Text>
          {formError.offerSignIn && (
            <Anchor c={ACCENT} size="md" component="button" type="button" mt={6} onClick={() => {
              loginForm.setValues({ email: registerForm.values.email.trim(), password: '' });
              goToStep(STEP.LOGIN);
            }}>
              Stattdessen anmelden
            </Anchor>
          )}
        </Alert>
      )}
    </>
  );

  const renderProblem = (problem) => (
    <Stack gap={16}>
      <Box>
        <Title order={3} c="#13135C">{problem.title}</Title>
        <Text size="md" c="#13135C" mt={16}>{problem.text}</Text>
      </Box>
      {problem.canEnterCode && (
        <form onSubmit={checkEnteredCode}>
          <Group align="flex-end" gap={8} wrap="nowrap">
            <TextInput
              label="Code"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={enteredCode}
              onChange={(event) => setEnteredCode(event.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button type="submit" color={ACCENT} disabled={!normalizeVoucherCode(enteredCode)}>Code prüfen</Button>
          </Group>
        </form>
      )}
      {problem.canRetry && (
        <Group>
          <Button color={ACCENT} onClick={() => setCheckAttempt((count) => count + 1)}>Erneut versuchen</Button>
        </Group>
      )}
      {problem.canSignIn && (
        <Group>
          <Button color={ACCENT} onClick={() => history.push(signedInEmail ? '/dashboard' : '/login')}>
            {signedInEmail ? 'Zu hellohousing' : 'Zur Anmeldung'}
          </Button>
        </Group>
      )}
    </Stack>
  );

  const renderChoose = () => (
    <Stack gap={20}>
      <Box>
        <Title order={2} c="#13135C">Aktivieren Sie Ihr Abonnement</Title>
        <Text size="md" c="#454576" mt={12}>Hier aktivieren Sie Ihr Abonnement für die digitale Immobilienverwaltung.</Text>
      </Box>
      <VoucherProduct voucher={voucher} code={code} />
      {signedInEmail ? (
        <Stack gap={12}>
          <Text size="md" c="#13135C">Sie sind angemeldet als <b>{signedInEmail}</b>.</Text>
          <Button color={ACCENT} fullWidth loading={busy} onClick={() => redeem(profile)}>Mit diesem Konto einlösen</Button>
          <Anchor c={ACCENT} size="md" component="button" type="button" ta="center" onClick={signOut}>
            Abmelden und ein anderes Konto nutzen
          </Anchor>
        </Stack>
      ) : (
        <Stack gap={12}>
          <Text size="md" c="#13135C">Erstellen Sie ein neues Konto oder melden Sie sich mit einem bestehenden hellohousing Konto an, um Ihr Abonnement zu aktivieren.</Text>
          <Button color={ACCENT} fullWidth onClick={() => goToStep(STEP.REGISTER)}>Neues Konto erstellen</Button>
          <Button variant="outline" color={ACCENT} fullWidth onClick={() => goToStep(STEP.LOGIN)}>Mit bestehendem Konto anmelden</Button>
        </Stack>
      )}
    </Stack>
  );

  const renderRegister = () => (
    <form onSubmit={registerForm.onSubmit((values) => register(values))} noValidate>
      <Stack gap={12}>
        <Title order={3} c="#13135C">Neues Konto erstellen</Title>
        <Text size="md" c="#13135C">Erstellen Sie bitte Ihre hellohousing Zugangsdaten. Im Anschluss aktivieren wir Ihr Abonnement für das neue Nutzerkonto.</Text>
        {renderFormMessages()}
        <TextInput type="email" label="E-Mail Adresse" autoComplete="email" required {...registerForm.getInputProps('email')} />
        <PasswordGuidanceInput label="Passwort" autoComplete="new-password" required {...registerForm.getInputProps('password')} />
        {isDevHost() && <PasswordInput label="Dev Passwort" required {...registerForm.getInputProps('devPassword')} />}
        <Checkbox
          {...registerForm.getInputProps('terms', { type: 'checkbox' })}
          label={
            <Text size="md" c="#13135C">
              Mit der Anmeldung zu hellohousing erkläre ich mich mit den{' '}
              <Anchor c={ACCENT} size="md" href="https://hellohousing.de/agb" target="_blank">AGB</Anchor>, dem{' '}
              <Anchor c={ACCENT} size="md" href={LEGAL_DOCUMENT_LINKS.dataProcessing} target="_blank">AVV</Anchor> und der{' '}
              <Anchor c={ACCENT} size="md" href={LEGAL_DOCUMENT_LINKS.dataPolicy} target="_blank">Datenschutzerklärung</Anchor> einverstanden.
            </Text>
          }
        />
        {renderChallenge()}
        {challenge === 0 && <Button type="submit" color={ACCENT} fullWidth loading={busy}>Konto erstellen</Button>}
        <Anchor c={ACCENT} size="md" component="button" type="button" ta="center" onClick={() => goToStep(STEP.CHOOSE)}>Zurück</Anchor>
      </Stack>
    </form>
  );

  const renderLogin = () => (
    <form onSubmit={loginForm.onSubmit((values) => signInAndRedeem(values))} noValidate>
      <Stack gap={12}>
        <Title order={3} c="#13135C">Mit bestehendem Konto anmelden</Title>
        <Text component="div" size="md" c="#13135C">Melden Sie sich an, um Ihren Code für hellohousing <PlanBadge plan={voucherPlan} inline /> einzulösen.</Text>
        {renderFormMessages()}
        <TextInput type="email" label="E-Mail Adresse" autoComplete="email" required {...loginForm.getInputProps('email')} />
        <PasswordInput label="Passwort" autoComplete="current-password" required {...loginForm.getInputProps('password')} />
        <Group justify="flex-end">
          <Anchor c={ACCENT} size="md" href="/forgotpassword">Passwort vergessen?</Anchor>
        </Group>
        {renderChallenge()}
        {challenge === 0 && <Button type="submit" color={ACCENT} fullWidth loading={busy}>Anmelden und Code einlösen</Button>}
        <Anchor c={ACCENT} size="md" component="button" type="button" ta="center" onClick={() => goToStep(STEP.CHOOSE)}>Zurück</Anchor>
      </Stack>
    </form>
  );

  const renderRedeemFailed = () => {
    const problem = getProblem(redeemFailure, { voucher, partner, failedOnRedeem: true });
    return (
      <Stack gap={16}>
        <Box>
          <Title order={3} c="#13135C">{problem.title}</Title>
          <Text size="md" c="#13135C" mt={16}>{problem.text}</Text>
        </Box>
        <Group>
          {problem.canRetry && <Button color={ACCENT} loading={busy} onClick={() => redeem(profile)}>Erneut versuchen</Button>}
          {problem.canUseOtherAccount && <Button color={ACCENT} onClick={() => { signOut(); goToStep(STEP.CHOOSE); }}>Abmelden und ein anderes Konto nutzen</Button>}
          <Button variant="outline" color={ACCENT} onClick={() => history.push('/dashboard')}>Zu hellohousing</Button>
        </Group>
      </Stack>
    );
  };

  const renderSuccess = () => {
    // The plan the code went to: the assignment's, which is the account's own plan when it was extended.
    const redeemedPlan = assignment?.subscriptionType?.name ? assignment.subscriptionType : voucherPlan;
    const validUntil = getPrepaidTermEnd(assignment);
    // A plan that already ran before today was extended rather than started.
    const extended = assignment?.startDate && moment(assignment.startDate).isBefore(moment(), 'day');
    return (
      <Stack gap={16}>
        <Title order={3} c="#13135C">Geschafft!</Title>
        <Text component="div" size="md" c="#13135C">
          {extended
            ? <>Ihr Code wurde eingelöst und Ihr Abonnement hellohousing <PlanBadge plan={redeemedPlan} inline />{voucher?.subscriptionType?.termPeriod ? ` um ${getTermLabel(voucher.subscriptionType)}` : ''} verlängert.</>
            : <>Ihr Code wurde eingelöst. Sie nutzen jetzt hellohousing <PlanBadge plan={redeemedPlan} inline />.</>}
        </Text>
        {validUntil && <Text size="md" c="#13135C">Ihr Abonnement ist gültig bis zum <b>{validUntil.format('DD.MM.YYYY')}</b>.</Text>}
        <Button color={ACCENT} fullWidth onClick={() => history.push('/dashboard')}>Zu hellohousing</Button>
      </Stack>
    );
  };

  const renderContent = () => {
    if (check.loading) {
      return (
        <Center py={40}>
          <Stack align="center" gap={10}>
            <Loader size="sm" color={ACCENT} />
            <Text size="md" c="#727293">Code wird geprüft …</Text>
          </Stack>
        </Center>
      );
    }
    if (step === STEP.SUCCESS) return renderSuccess();
    if (step === STEP.REDEEM_FAILED) return renderRedeemFailed();
    if (check.state !== VOUCHER_STATE.VALID) {
      return renderProblem(getProblem(check.state, { voucher, partner, failedOnRedeem: false }));
    }
    if (step === STEP.REGISTER) return renderRegister();
    if (step === STEP.LOGIN) return renderLogin();
    return renderChoose();
  };

  return (
    <Box className={classes.page}>
      <Stack align="center" gap={32} className={classes.column}>
        {partner ? <PartnerBranding partner={partner} logoHeight={136} /> : <img src="/hello-housing-logo.svg" alt="hellohousing Logo" className={classes.hellohousingLogo} />}
        <Card shadow="xs" radius={8} p={32} className={classes.card}>
          <LoadingOverlay visible={busy && step !== STEP.CHOOSE && step !== STEP.REDEEM_FAILED} />
          {renderContent()}
        </Card>
      </Stack>
    </Box>
  );
};

export default Redeem;
